// src/app/pages/user/CloneGroupsPanel.js
// Renders inline inside GroupTransferList (not as its own Modal) -
// a nested Modal-on-Modal stacking looked wrong when this was opened
// from within another modal (Edit Groups / Add New User).
import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { getUsersList } from '../api/api';
import { showErrorToast } from '../../components/commonfunction';

const CloneGroupsPanel = ({ currentAssigned = [], onApply, onCancel }) => {
    const [users, setUsers] = useState([]);
    const [filter, setFilter] = useState('');
    const [debouncedFilter, setDebouncedFilter] = useState('');
    const [filtering, setFiltering] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [mode, setMode] = useState('replace');
    const filterTimerRef = useRef(null);
    const hasFetchedRef = useRef(false);

    const fetchUsers = () => {
        setLoading(true);
        getUsersList()
            .then((r) => {
                if (!r) return null;
                return r.json();
            })
            .then((data) => {
                if (!data) return;
                let rows = [];
                if (Array.isArray(data)) {
                    rows = data;
                } else if (data && Array.isArray(data.dataTable)) {
                    rows = data.dataTable;
                }
                setUsers(rows);
            })
            .catch(() => showErrorToast('Failed to load users'))
            .finally(() => setLoading(false));
    };

    const handleFilterChange = (value) => {
        setFilter(value);

        if (value.length === 0) {
            if (filterTimerRef.current) {
                clearTimeout(filterTimerRef.current);
            }
            setFiltering(false);
            setDebouncedFilter('');
            return;
        }

        // Users are only fetched once the user actually starts typing (1+
        // characters), not eagerly when the panel opens - defers the
        // full-list fetch until there's real search intent, and only ever
        // once per panel open (hasFetchedRef), since getUsersList() has no
        // server-side search param - refetching per keystroke would return
        // the same data.
        if (!hasFetchedRef.current) {
            hasFetchedRef.current = true;
            fetchUsers();
        }

        setFiltering(true);
        if (filterTimerRef.current) {
            clearTimeout(filterTimerRef.current);
        }
        filterTimerRef.current = setTimeout(() => {
            setDebouncedFilter(value);
            setFiltering(false);
        }, 300);
    };

    // Mount-once reset - this component is only mounted while the panel is shown.
    useEffect(() => {
        hasFetchedRef.current = false;
        setUsers([]);
        setLoading(false);
        setSelectedUser(null);
        setFilter('');
        setDebouncedFilter('');
        setFiltering(false);
        setMode('replace');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filteredUsers = users.filter((u) => {
        const q = debouncedFilter.toLowerCase();
        return (
            (u.name || '').toLowerCase().includes(q) ||
            (u.username || '').toLowerCase().includes(q) ||
            (u.email || '').toLowerCase().includes(q)
        );
    });

    const sourceGroupNames = selectedUser
        ? (selectedUser.usergroups || [])
              .map((g) => (typeof g === 'string' ? g : g.name))
              .filter(Boolean)
        : [];

    const toAdd = sourceGroupNames.filter((n) => !currentAssigned.includes(n));
    const toRemove =
        mode === 'replace'
            ? currentAssigned.filter((n) => !sourceGroupNames.includes(n))
            : [];

    const handleApply = () => {
        const result =
            mode === 'replace'
                ? sourceGroupNames
                : [...currentAssigned, ...toAdd];
        onApply(result);
    };

    return (
        <div
            className='clone-groups-panel'
            style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
        >
            <div className='gtl-toolbar' style={{ marginTop: 16 }}>
                <button
                    type='button'
                    className='btn btn-sm'
                    onClick={onCancel}
                    title='Back to groups'
                >
                    <i className='fa-light fa-arrow-left' />
                    Back
                </button>
                <strong
                    className='d-flex align-items-center'
                    style={{ fontSize: 14, color: '#464E5F' }}
                >
                    Clone Groups From User
                </strong>
            </div>

            <div className='position-relative mb-2'>
                <input
                    type='text'
                    className='form-control'
                    placeholder='Search by name, username, or email…'
                    value={filter}
                    onChange={(e) => handleFilterChange(e.target.value)}
                    autoFocus
                    style={{ paddingRight: 32 }}
                />
                {(loading || filtering) && (
                    <span
                        className='spinner-border spinner-border-sm'
                        style={{
                            position: 'absolute',
                            right: 10,
                            top: 'calc(50% - 8px)',
                            color: '#464E5F',
                        }}
                    />
                )}
            </div>

            {filter.length > 0 && !loading && !filtering && (
                <div
                    className='border rounded mb-3'
                    style={{ maxHeight: 140, overflowY: 'auto' }}
                >
                    {filteredUsers.length === 0 && (
                        <div
                            className='p-2 text-center text-muted'
                            style={{ fontSize: 12 }}
                        >
                            No users found
                        </div>
                    )}
                    {filteredUsers.map((u) => (
                        <div
                            key={u.userid || u.username || u.email}
                            className={
                                selectedUser === u
                                    ? 'bg-primary text-white'
                                    : ''
                            }
                            style={{
                                padding: '6px 12px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #eee',
                                fontSize: 13,
                            }}
                            onClick={() => setSelectedUser(u)}
                        >
                            <strong>{u.name || u.username}</strong>
                            {u.email && (
                                <span
                                    className='ml-2'
                                    style={{ fontSize: 11, opacity: 0.75 }}
                                >
                                    {u.email}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {selectedUser && (
                <>
                    {sourceGroupNames.length === 0 && (
                        <div
                            className='alert alert-warning py-2 mb-2'
                            style={{ fontSize: 12 }}
                        >
                            No group data found for this user. They may have no
                            groups assigned.
                        </div>
                    )}
                    <div className='mb-3'>
                        <strong style={{ fontSize: 13 }}>Mode: </strong>
                        <label
                            className='ml-3 mr-3'
                            style={{ fontSize: 13, fontWeight: 'normal' }}
                        >
                            <input
                                type='radio'
                                name='cloneMode'
                                value='replace'
                                checked={mode === 'replace'}
                                onChange={() => setMode('replace')}
                                className='mr-1'
                            />
                            Replace
                        </label>
                        <label style={{ fontSize: 13, fontWeight: 'normal' }}>
                            <input
                                type='radio'
                                name='cloneMode'
                                value='merge'
                                checked={mode === 'merge'}
                                onChange={() => setMode('merge')}
                                className='mr-1'
                            />
                            Merge
                        </label>
                    </div>

                    <div className='d-flex' style={{ gap: 8 }}>
                        <div style={{ flex: 1 }}>
                            <div
                                className='mb-1 text-uppercase text-muted'
                                style={{ fontSize: 10, letterSpacing: 1 }}
                            >
                                Will add ({toAdd.length})
                            </div>
                            <div
                                className='border rounded p-2'
                                style={{
                                    minHeight: 50,
                                    maxHeight: 80,
                                    overflowY: 'auto',
                                    fontSize: 12,
                                }}
                            >
                                {toAdd.length === 0 ? (
                                    <span className='text-muted'>None</span>
                                ) : (
                                    toAdd.map((n) => (
                                        <div
                                            key={n}
                                            style={{ color: '#155724' }}
                                        >
                                            + {n}
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                        {mode === 'replace' && (
                            <div style={{ flex: 1 }}>
                                <div
                                    className='mb-1 text-uppercase text-muted'
                                    style={{ fontSize: 10, letterSpacing: 1 }}
                                >
                                    Will remove ({toRemove.length})
                                </div>
                                <div
                                    className='border rounded p-2'
                                    style={{
                                        minHeight: 50,
                                        maxHeight: 80,
                                        overflowY: 'auto',
                                        fontSize: 12,
                                    }}
                                >
                                    {toRemove.length === 0 ? (
                                        <span className='text-muted'>None</span>
                                    ) : (
                                        toRemove.map((n) => (
                                            <div
                                                key={n}
                                                style={{ color: '#721c24' }}
                                            >
                                                − {n}
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}

            <div className='d-flex footer-btn justify-content-end mt-auto pt-3'>
                <Button
                    variant='primary'
                    onClick={handleApply}
                    disabled={!selectedUser}
                >
                    Apply
                </Button>
            </div>
        </div>
    );
};

export default CloneGroupsPanel;
